/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../actions/snackbarActions";
import axios from "axios";
import { config } from "../../../Helpers/env";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import { history } from "../../../Helpers/history";
import ObGynSidebar from "../Utils/ObGynSidebar";
import ObGynPatientInfo from "../Utils/ObGynPatientInfo";
import {
    DashboardContainer,
    SectionHeader,
    SectionIcon,
    SectionTitle,
    SectionSubtitle,
    ObstetricSummaryCard,
    SummaryCardHeader,
    SummaryCardTitle,
    StatusBadge,
    SummaryGrid,
    SummaryItem,
    SummaryLabel,
    SummaryValue,
    ProgramsRow,
    ProgramPanel,
    ProgramPanelHeader,
    ProgramPanelTitle,
    ProgramPanelBody,
    WorkflowItem,
    WorkflowStepNumber,
    WorkflowItemText,
    WorkflowItemTitle,
    WorkflowItemDesc,
    WorkflowItemBadge,
    CtaCard,
    CtaContent,
    CtaTitle,
    CtaDescription,
    CtaButton,
} from "./ObGynDashboardStyles";

/* ─── Check Icon for completed steps ────────────────────── */
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="14" height="14">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" />
    </svg>
);

/**
 * ObGynDashboard — Clinical hub for OB/GYN doctor actions.
 *
 * Layout:
 *  1. Compact Patient Info Strip (single row)
 *  2. ANC Obstetric Summary (if booked) OR booking CTA
 *  3. Two-column program panels: Obstetrics workflow + Gynaecology services
 *
 * Note: Clinical actions (Triage, Lab, Rx, etc.) are in the right mini sidebar.
 */
const ObGynDashboard = ({ actions, snackbar, match: { params } }) => {
    const { patient_number, visit_id, patient_type } = params;

    /* ── Current user ── */
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = `${!_token.first_name ? "" : _token.first_name} ${
        !_token.last_name ? "" : _token.last_name
    }`;

    /* ── ANC summary state ── */
    const [ancSummary, setAncSummary] = useState({
        hasActiveBooking: false,
        edd: null,
        gestationalAge: null,
        contactsDone: 0,
        gravida: null,
        parity: null,
        ancNumber: null,
        status: null,
        lnmp: null,
        bloodGroup: null,
        rhesus: null,
    });

    /* ── Fetch ANC summary if booking exists ── */
    useEffect(() => {
        if (!patient_number) return;
        axios
            .get(`${config.smsUrl}/obgyn/anc/booking/${patient_number}`)
            .then((res) => {
                const data = res.data || {};
                if (data.id) {
                    let ga = null;
                    if (data.lnmp) {
                        const lnmpDate = new Date(data.lnmp);
                        const today = new Date();
                        const diffMs = today - lnmpDate;
                        const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
                        const diffDays = Math.floor(
                            (diffMs % (7 * 24 * 60 * 60 * 1000)) / (24 * 60 * 60 * 1000)
                        );
                        ga = `${diffWeeks}+${diffDays}`;
                    }
                    setAncSummary({
                        hasActiveBooking: true,
                        edd: data.edd,
                        gestationalAge: ga || data.gestational_age_at_booking,
                        contactsDone: data.contacts_done || 0,
                        gravida: data.gravida,
                        parity: data.parity,
                        ancNumber: data.anc_number,
                        status: data.status || "active",
                        lnmp: data.lnmp,
                        bloodGroup: data.blood_group,
                        rhesus: data.rhesus_factor,
                    });
                }
            })
            .catch(() => {});
    }, [patient_number]);

    /* ── Navigation helper ── */
    const goTo = (slug) => {
        history.push(
            `/gyn-and-obs-${slug}/${patient_number}/${visit_id}/${patient_type}`
        );
    };

    /* ── Snackbar ── */
    const closeSnackbar = () => actions.snackbarActions.hideSnackbar();
    const { openBar, type, message } = snackbar;

    /* ── Obstetric workflow steps ── */
    const obstetricSteps = [
        {
            num: 1,
            title: "ANC Registration & Booking",
            desc: "Register into ANC, record LNMP, obstetric history",
            available: true,
            slug: "antenatal-card",
            done: ancSummary.hasActiveBooking,
        },
        {
            num: 2,
            title: "ANC Clinical Assessment",
            desc: "Vitals, abdominal exam, urinalysis per contact",
            available: ancSummary.hasActiveBooking,
            slug: "anc-assessment",
        },
        {
            num: 3,
            title: "ANC Visit Schedule (8 Contacts)",
            desc: "WHO/Uganda 8-contact schedule with tracking",
            available: ancSummary.hasActiveBooking,
            slug: "anc-schedule",
        },
        {
            num: 4,
            title: "Screening & Investigations",
            desc: "Lab tests: HIV, syphilis, Hb, blood group, ultrasound",
            available: ancSummary.hasActiveBooking,
            slug: "anc-investigations",
        },
        {
            num: 5,
            title: "Preventive Treatments",
            desc: "Td vaccine, IPTp-SP, iron/folic acid, LLIN, deworming",
            available: ancSummary.hasActiveBooking,
            slug: "anc-preventive",
        },
        {
            num: 6,
            title: "Birth Preparedness & Danger Signs",
            desc: "Birth plan, companion, transport, danger sign counselling",
            available: ancSummary.hasActiveBooking,
            slug: "birth-preparedness",
        },
        {
            num: 7,
            title: "eMTCT — HIV in Pregnancy",
            desc: "HIV testing, ART tracking, partner testing, syphilis",
            available: ancSummary.hasActiveBooking,
            slug: "emtct",
        },
        {
            num: 8,
            title: "Labour Ward & Partograph",
            desc: "Admission, labour monitoring, digital partograph",
            available: ancSummary.hasActiveBooking,
            slug: "labour-ward",
        },
        {
            num: 9,
            title: "Delivery & Newborn Record",
            desc: "Delivery record, APGAR scoring, mother-baby pairing",
            available: false,
        },
        {
            num: 10,
            title: "Postnatal Care",
            desc: "Maternal and newborn postnatal assessments",
            available: false,
        },
    ];

    /* ── Gynaecology services ── */
    const gynServices = [
        {
            num: 1,
            title: "Gynaecological Consultation",
            desc: "History, examination, and management",
            slug: "gynae-consultation",
            available: true,
        },
        {
            num: 2,
            title: "Cervical Cancer Screening",
            desc: "VIA/VILI, HPV testing, Pap smear, cryotherapy",
            slug: "cervical-screening",
            available: true,
        },
        {
            num: 3,
            title: "GBV Screening & Management",
            desc: "Screening, documentation, referrals",
            slug: "gbv-screening",
            available: true,
        },
        {
            num: 4,
            title: "Family Planning",
            desc: "Counselling, method provision, follow-up",
            available: false,
        },
    ];

    return (
        <div className="journals">
            <PageTitle title="OB/GYN Dashboard" />
            <SubHeader
                title="OB/GYN"
                subTitle="Patient Dashboard"
                doctor_name={doctor_name}
            >
                <div className="header-icon-margin-right-zero">
                    <img
                        src="/images/antenatal-blue.png"
                        className="icon-resize surgery-icon-tabs ante-natal-green"
                        alt="OB/GYN"
                    />
                </div>
            </SubHeader>
            <MainSnackbar
                variant={type}
                open={openBar}
                message={message}
                handleCloseBar={closeSnackbar}
            />

            <ObGynSidebar
                patient_number={patient_number}
                visit_id={visit_id}
                patient_type={patient_type}
            >
                <DashboardContainer>
                    {/* ═══ COMPACT PATIENT INFO STRIP ═══ */}
                    <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} />

                    {/* ═══ ANC OBSTETRIC SUMMARY OR BOOKING CTA ═══ */}
                    {ancSummary.hasActiveBooking ? (
                        <ObstetricSummaryCard>
                            <SummaryCardHeader>
                                <SummaryCardTitle>Antenatal Care Summary</SummaryCardTitle>
                                <StatusBadge status={ancSummary.status}>
                                    {ancSummary.status || "Active"}
                                </StatusBadge>
                            </SummaryCardHeader>
                            <SummaryGrid>
                                <SummaryItem highlight>
                                    <SummaryLabel>ANC Number</SummaryLabel>
                                    <SummaryValue size="large" color="#3871B7">
                                        {ancSummary.ancNumber || "—"}
                                    </SummaryValue>
                                </SummaryItem>
                                <SummaryItem highlight>
                                    <SummaryLabel>Gestational Age</SummaryLabel>
                                    <SummaryValue size="large">
                                        {ancSummary.gestationalAge
                                            ? `${ancSummary.gestationalAge} wks`
                                            : "—"}
                                    </SummaryValue>
                                </SummaryItem>
                                <SummaryItem>
                                    <SummaryLabel>EDD</SummaryLabel>
                                    <SummaryValue>{ancSummary.edd || "—"}</SummaryValue>
                                </SummaryItem>
                                <SummaryItem>
                                    <SummaryLabel>Gravida / Parity</SummaryLabel>
                                    <SummaryValue>
                                        G{ancSummary.gravida || "—"} P
                                        {ancSummary.parity || "—"}
                                    </SummaryValue>
                                </SummaryItem>
                                <SummaryItem>
                                    <SummaryLabel>ANC Contacts</SummaryLabel>
                                    <SummaryValue>
                                        {ancSummary.contactsDone} / 8
                                    </SummaryValue>
                                </SummaryItem>
                                <SummaryItem>
                                    <SummaryLabel>Blood Group</SummaryLabel>
                                    <SummaryValue>
                                        {ancSummary.bloodGroup || "—"}
                                        {ancSummary.rhesus
                                            ? ` (${ancSummary.rhesus})`
                                            : ""}
                                    </SummaryValue>
                                </SummaryItem>
                            </SummaryGrid>
                        </ObstetricSummaryCard>
                    ) : (
                        <CtaCard borderColor="#3871B7">
                            <CtaContent>
                                <CtaTitle>No Active ANC Booking</CtaTitle>
                                <CtaDescription>
                                    This patient is not yet registered in the Antenatal Care
                                    program. Register now to begin tracking the pregnancy,
                                    record LNMP, and generate the ANC visit schedule.
                                </CtaDescription>
                            </CtaContent>
                            <CtaButton onClick={() => goTo("antenatal-card")}>
                                Register for ANC
                            </CtaButton>
                        </CtaCard>
                    )}

                    {/* ═══ OB/GYN PROGRAMS — TWO COLUMNS ═══ */}
                    <SectionHeader color="#3871B7">
                        <SectionIcon color="#3871B7">♀</SectionIcon>
                        <SectionTitle>OB/GYN Programs</SectionTitle>
                        <SectionSubtitle>Specialty care pathways</SectionSubtitle>
                    </SectionHeader>

                    <ProgramsRow>
                        {/* ── LEFT: Obstetric Care Pathway ── */}
                        <ProgramPanel borderColor="#3871B7">
                            <ProgramPanelHeader>
                                <ProgramPanelTitle color="#3871B7">
                                    Obstetric Care
                                </ProgramPanelTitle>
                                <span
                                    style={{
                                        marginLeft: "auto",
                                        fontSize: "11px",
                                        color: "#6f7077",
                                    }}
                                >
                                    {obstetricSteps.filter((s) => s.available).length} of{" "}
                                    {obstetricSteps.length} available
                                </span>
                            </ProgramPanelHeader>
                            <ProgramPanelBody>
                                {obstetricSteps.map((step) => (
                                    <WorkflowItem
                                        key={step.num}
                                        available={step.available}
                                        onClick={() =>
                                            step.available && step.slug && goTo(step.slug)
                                        }
                                    >
                                        <WorkflowStepNumber
                                            available={step.available}
                                            done={step.done}
                                            color="#3871B7"
                                        >
                                            {step.done ? <CheckIcon /> : step.num}
                                        </WorkflowStepNumber>
                                        <WorkflowItemText>
                                            <WorkflowItemTitle available={step.available}>
                                                {step.title}
                                            </WorkflowItemTitle>
                                            <WorkflowItemDesc>{step.desc}</WorkflowItemDesc>
                                        </WorkflowItemText>
                                        {step.done && (
                                            <WorkflowItemBadge variant="done">
                                                Completed
                                            </WorkflowItemBadge>
                                        )}
                                        {!step.available && !step.done && (
                                            <WorkflowItemBadge variant="upcoming">
                                                Upcoming
                                            </WorkflowItemBadge>
                                        )}
                                    </WorkflowItem>
                                ))}
                            </ProgramPanelBody>
                        </ProgramPanel>

                        {/* ── RIGHT: Gynaecology Services ── */}
                        <ProgramPanel borderColor="#c2185b">
                            <ProgramPanelHeader>
                                <ProgramPanelTitle color="#c2185b">
                                    Gynaecology Services
                                </ProgramPanelTitle>
                                <span
                                    style={{
                                        marginLeft: "auto",
                                        fontSize: "11px",
                                        color: "#6f7077",
                                    }}
                                >
                                    {gynServices.filter((s) => s.available).length} of{" "}
                                    {gynServices.length} available
                                </span>
                            </ProgramPanelHeader>
                            <ProgramPanelBody>
                                {gynServices.map((service) => (
                                    <WorkflowItem
                                        key={service.num}
                                        available={service.available}
                                        onClick={() =>
                                            service.available &&
                                            service.slug &&
                                            goTo(service.slug)
                                        }
                                    >
                                        <WorkflowStepNumber
                                            available={service.available}
                                            color="#c2185b"
                                        >
                                            {service.num}
                                        </WorkflowStepNumber>
                                        <WorkflowItemText>
                                            <WorkflowItemTitle available={service.available}>
                                                {service.title}
                                            </WorkflowItemTitle>
                                            <WorkflowItemDesc>
                                                {service.desc}
                                            </WorkflowItemDesc>
                                        </WorkflowItemText>
                                        {!service.available && (
                                            <WorkflowItemBadge variant="upcoming">
                                                Upcoming
                                            </WorkflowItemBadge>
                                        )}
                                    </WorkflowItem>
                                ))}
                            </ProgramPanelBody>
                        </ProgramPanel>
                    </ProgramsRow>
                </DashboardContainer>
            </ObGynSidebar>
        </div>
    );
};

function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            snackbarActions: bindActionCreators(snackbarActions, dispatch),
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ObGynDashboard);
